import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Top_Menu from './Top_Menu';
import Bottom_Menu from './Bottom_Menu';
import { useTransitionNavigate } from './PageTransition';
import lang from '../language/Eng_eng.json';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';

// Global Styles for the Home page
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500;1,600;1,700;1,800&display=swap');

  body {
    margin: 0;
    padding: 0;
    font-family: 'EB Garamond', serif;
    background-color: #F3EFE6;
    color: #5C4033;
    overflow-x: hidden;
  }

  h1, h2, h3, h4 {
    font-family: 'EB Garamond', serif;
    text-transform: uppercase;
    margin: 0;
    font-weight: 500;
  }

  a {
    text-decoration: none;
    color: inherit;
  }
`;

const HomeContainer = styled.div`
  width: 100%;
  padding-top: 100px;

  @media (max-width: 768px) {
    padding-top: 145px;
  }
`;

// --- Hero Section ---
const HeroSection = styled.section`
  display: flex;
  height: 80vh;
  width: 100%;
  
  @media (max-width: 1024px) {
    flex-direction: column-reverse;
    height: auto;
  }
`;

const HeroTextSide = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 5%;
  background-color: #F3EFE6;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    width: 100%;
    padding: 60px 20px;
  }
`;

const HeroImageSide = styled.div`
  width: 50%;
  background: url('/image/Trent-van-der-Jagt-4058-2048x1366.jpg');
  background-size: cover;
  background-position: center;

  @media (max-width: 1024px) {
    width: 100%;
    height: 60vh;
  }
`;

const HeroHeadline = styled.h1`
  font-size: 2.5rem;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: 0.12rem;
  margin-bottom: 2.5rem;
  color: #5C4033;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  margin-bottom: 0;
  max-width: 700px;
  color: #5C4033;
  opacity: 0.85;
`;

const ActionButton = styled.a`
  padding: 12px 35px;
  border: 1px solid #BCA18D;
  color: #5C4033;
  font-family: 'EB Garamond', serif;
  font-size: 1.0rem;
  font-weight: 500;
  letter-spacing: 0.18rem;
  text-transform: uppercase;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: #5C4033;
    color: #F3EFE6;
  }
`;

// --- What's On Section ---
const WhatsOnSection = styled.section`
  padding: 80px 10%;
  background-color: #F3EFE6;
  border-top: 1px solid #EAE5D9;
`;

const WhatsOnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  border-bottom: 1px solid #EAE5D9;
  padding-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const WhatsOnTitle = styled.h2`
  font-family: 'EB Garamond', serif;
  font-size: 2.3rem;
  font-weight: 500;
  color: #5C4033;
  letter-spacing: 0.1rem;
`;

const SeeAllLink = styled.span`
  font-family: 'EB Garamond', serif;
  font-size: 0.95rem;
  font-weight: 500;
  color: #8C7561;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: #5C4033;
  }
`;

const NewsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 0;
  border-bottom: 1px solid #EAE5D9;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const NewsDate = styled.div`
  flex: 0 0 150px;
  font-family: 'EB Garamond', serif;
  font-size: 1.0rem;
  color: #8C7561;
`;

const NewsTitleLine = styled.div`
  flex: 1;
  font-family: 'EB Garamond', serif;
  font-size: 1.35rem;
  font-weight: 500;
  color: #5C4033;
  padding-right: 2rem;
`;

const ReadMoreButton = styled.button`
  padding: 10px 25px;
  border: 1px solid #5C4033;
  background: transparent;
  color: #5C4033;
  font-family: 'EB Garamond', serif;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #5C4033;
    color: #F3EFE6;
  }
`;

// --- Our Story Section ---
const StorySection = styled.section`
  display: flex;
  min-height: 80vh;
  width: 100%;
  background-color: #F3EFE6;
  padding: 80px 5%;
  box-sizing: border-box;
  align-items: center;
  gap: 5%;
  border-top: 2px solid #BCA18D;

  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 60px 20px;
    gap: 40px;
  }
`;

const StoryImageSide = styled.div`
  flex: 1.3;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: auto;
    max-height: 600px;
    object-fit: cover;
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }
`;

const StoryTextSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 2%;

  @media (max-width: 1024px) {
    align-items: center;
    text-align: center;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.3rem;
  font-weight: 500;
  margin-bottom: 2rem;
  color: #5C4033;
  letter-spacing: 0.1rem;
`;

const StoryText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: #5C4033;
  margin-bottom: 2.5rem;
  max-width: 600px;
  opacity: 0.85;
`;

// --- Experience/Gallery Section ---
const ExperienceSection = styled.section`
  padding: 100px 5%;
  background-color: #F3EFE6;
  border-bottom: 2px solid #BCA18D;
`;

const ExperienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const ExperienceItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ExperienceImage = styled.img`
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  margin-bottom: 30px;
`;

const ExperienceTitle = styled.h3`
  font-size: 1.7rem;
  font-weight: 500;
  color: #5C4033;
  margin-bottom: 15px;
  letter-spacing: 0.1rem;
`;

const ExperienceText = styled.p`
  font-size: 1.0rem;
  line-height: 1.7;
  color: #5C4033;
  margin-bottom: 30px;
  opacity: 0.85;
  max-width: 350px;
  white-space: pre-line;
  flex-grow: 1; // This pushes the button to the bottom
`;

interface Post {
  id: string;
  title: string;
  createdAt: any;
}

const Home: React.FC = () => {
  const [latestNews, setLatestNews] = useState<Post[]>([]);
  const navigate = useNavigate();
  const { navigateTo } = useTransitionNavigate();

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'), limit(3));
        const snapshot = await getDocs(q);
        const newsData: Post[] = [];
        snapshot.forEach((doc) => {
          newsData.push({ id: doc.id, ...doc.data() } as Post);
        });
        setLatestNews(newsData);
      } catch (error) {
        console.error("Error fetching news:", error);
      }
    };
    fetchNews();
  }, []);

  const handleReadMore = (postId: string) => {
    navigate('/news', { state: { postId } });
  };

  return (
    <>
      <GlobalStyle />
      <HomeContainer>
        <Top_Menu />

        {/* Hero Section */}
        <HeroSection id="home">
          <HeroTextSide>
            <HeroHeadline>{lang.home.hero.headline}</HeroHeadline>
            <HeroDescription>
              {lang.home.hero.description}
            </HeroDescription>
          </HeroTextSide>
          <HeroImageSide />
        </HeroSection>


        {/* Our Story Section */}
        <StorySection id="story">
          <StoryImageSide>
            <img src="/image/Trent-van-der-Jagt-4058-2048x1366.jpg" alt="Allta Story" />
          </StoryImageSide>
          <StoryTextSide>
            <SectionTitle>{lang.home.story.title}</SectionTitle>
            <StoryText>
              {lang.home.story.text}
            </StoryText>
            <ActionButton
              href="https://www.opentable.com.au/booking/restref/availability?lang=en-AU&correlationId=2137dfd1-2106-4942-91d0-1229cda41100&restRef=263042&otSource=Restaurant%20website"
              target="_blank"
              rel="noopener noreferrer"
            >
              {lang.home.story.action}
            </ActionButton>
          </StoryTextSide>
        </StorySection>

        {/* The Experience Section */}
        <ExperienceSection id="experience">
          <ExperienceGrid>
            <ExperienceItem>
              <ExperienceImage src="/image/Mask-group-8.png" alt="Food" />
              <ExperienceTitle>{lang.home.experience.food.title}</ExperienceTitle>
              <ExperienceText>{lang.home.experience.food.text}</ExperienceText>
              <ActionButton
                href="https://vuzagroup.synology.me/allta_menu/index.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                {lang.home.experience.food.action}
              </ActionButton>
            </ExperienceItem>
            <ExperienceItem>
              <ExperienceImage src="/image/Mask-group-7.png" alt="Beverages" />
              <ExperienceTitle>{lang.home.experience.beverages.title}</ExperienceTitle>
              <ExperienceText>{lang.home.experience.beverages.text}</ExperienceText>
              <ActionButton href="#">{lang.home.experience.beverages.action}</ActionButton>
            </ExperienceItem>
            <ExperienceItem>
              <ExperienceImage src="/image/Mask-group-6.png" alt="Contact" />
              <ExperienceTitle>{lang.home.experience.contact.title}</ExperienceTitle>
              <ExperienceText>{lang.home.experience.contact.text}</ExperienceText>
              <ActionButton
                href="#"
                onClick={(e) => { e.preventDefault(); navigateTo('/contact'); }}
              >
                {lang.home.experience.contact.action}
              </ActionButton>
            </ExperienceItem>
          </ExperienceGrid>
        </ExperienceSection>

        {/* What's On Section */}
        <WhatsOnSection>
          <WhatsOnHeader>
            <WhatsOnTitle>WHAT'S ON</WhatsOnTitle>
            <SeeAllLink onClick={() => navigate('/news')}>SEE ALL UPCOMING EVENTS →</SeeAllLink>
          </WhatsOnHeader>

          {latestNews.map(post => (
            <NewsRow key={post.id}>
              <NewsDate>
                {post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                }) : ''}
              </NewsDate>
              <NewsTitleLine>{post.title}</NewsTitleLine>
              <ReadMoreButton onClick={() => handleReadMore(post.id)}>READ MORE</ReadMoreButton>
            </NewsRow>
          ))}
          {latestNews.length === 0 && (
            <p style={{ textAlign: 'center', opacity: 0.6, marginTop: '2rem' }}>No upcoming events.</p>
          )}
        </WhatsOnSection>

        <Bottom_Menu />
      </HomeContainer>
    </>
  );
};

export default Home;
